import SEO from '../components/SEO';
import React from 'react';
import { motion } from 'framer-motion';
import { Award, Users, Target, TrendingUp, Heart, Zap, Shield, Globe, CheckCircle2, Cloud } from 'lucide-react';

const AboutUsPage = () => {
  const stats = [
    { value: "100%", label: "Dedicated to SAP Business One" },
    { value: "4.9/5", label: "Customer Satisfaction" },
    { value: "15+", label: "Years Partnership" }
  ];

  const values = [
    { icon: Heart, title: "Customer Success First", desc: "Your success is our success. We're not satisfied until you're thriving." },
    { icon: Users, title: "Partnership Mindset", desc: "We work alongside you as an extension of your team, not just a vendor." },
    { icon: Target, title: "Excellence in Delivery", desc: "15+ years of proven expertise delivering successful transformations." },
    { icon: Zap, title: "Innovation Driven", desc: "Continuously evolving with the latest SAP, AWS, and industry technologies." }
  ];

  const partners = [
    { name: 'SAP', role: 'Premier Partner', desc: 'Premier SAP Business One partner in the UK with 15+ years of expertise and multiple SAP awards for excellence.', benefits: ['20+ SAP Business One awards', '500+ successful implementations', 'Certified SAP consultants', 'Direct SAP relationship'] },
    { name: 'AWS', role: 'Advanced Partner', desc: 'AWS Advanced Tier Services Partner delivering enterprise-grade cloud hosting with guaranteed 99.995% uptime.', benefits: ['99.995% uptime guarantee', '9 global datacentre locations', '24x7 monitoring and support', 'Enterprise security standards'] },
    { name: 'Boyum', role: 'Premier Partner', desc: 'Premier Partner for Boyum Solutions delivering manufacturing and productivity extensions to 900+ customers worldwide.', benefits: ['900+ manufacturing customers', 'Beas Manufacturing expertise', 'B1 Usability Package specialists', 'Certified Boyum consultants'] }
  ];

  return (
    <div className="min-h-screen bg-white">
      <SEO 
        title="About Ascarii | SAP Premier Partner & AWS Advanced Partner"
        description="SAP Premier Partner since 2010. 15+ years helping UK SMEs transform with SAP Business One. 4.9/5 customer rating. Your partner for business transformation."
        keywords="about Ascarii, SAP partner UK, ERP partner, SAP Premier Partner, AWS Advanced Partner"
        canonical="https://www.ascarii.com/about"
      />

      {/* Hero */}
      <section className="relative min-h-[700px] flex items-center overflow-hidden bg-gradient-to-br from-blue-700 via-sky-800 to-blue-900">
        <div className="absolute inset-0 z-0">
          <img src="https://images.unsplash.com/photo-1522071820081-009f0129c71c?w=1920&q=85" alt="Ascarii Team" className="w-full h-full object-cover opacity-30" loading="eager" />
        </div>
        <div className="container mx-auto px-4 lg:px-8 py-20 relative z-10">
          <div className="max-w-4xl">
            <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
              <div className="inline-block px-4 py-2 bg-white/20 backdrop-blur-sm border border-white/30 rounded-full text-sm font-semibold mb-6 text-white">About Ascarii</div>
              <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold mb-6 leading-tight text-white">Your Partner for Business Transformation</h1>
              <p className="text-xl md:text-2xl text-blue-100 mb-8 leading-relaxed">For over 15+ years, we've been helping businesses transform with SAP Business One. We're not just consultants - we're partners who genuinely care about your success.</p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-4xl mx-auto">
            {stats.map((stat, i) => (
              <motion.div key={i} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: i * 0.1 }} className="text-center">
                <div className="text-4xl md:text-5xl font-bold text-blue-600 mb-2">{stat.value}</div>
                <div className="text-sm text-gray-600 font-medium">{stat.label}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* How It All Began - Complete Redesign */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-stretch">
            <motion.div initial={{ opacity: 0, x: -30 }} whileInView={{ opacity: 1, x: 0 }} transition={{ duration: 0.6 }} viewport={{ once: true }} className="relative group flex">
              <div className="relative rounded-2xl overflow-hidden shadow-2xl w-full">
                <img src="https://images.unsplash.com/photo-1560179707-f14e90ef3623?w=800&q=85" alt="Business Growth Journey" className="w-full h-full object-cover transform group-hover:scale-105 transition-transform duration-500" loading="lazy" />
                <div className="absolute inset-0 bg-gradient-to-br from-blue-600/20 to-transparent" />
              </div>
            </motion.div>
            
            <motion.div initial={{ opacity: 0, x: 30 }} whileInView={{ opacity: 1, x: 0 }} transition={{ duration: 0.6 }} viewport={{ once: true }} className="flex flex-col justify-center">
              <span className="inline-block px-4 py-2 bg-blue-100 text-blue-600 rounded-full text-sm font-semibold mb-4 w-fit">Our Story</span>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">15+ Years of Partnership Excellence</h2>
              <p className="text-lg text-gray-600 mb-6 leading-relaxed">
                From day one, our mission has been clear: transform growing businesses with the right technology and unwavering support.
              </p>
              <div className="space-y-4">
                <div className="flex items-start space-x-3">
                  <CheckCircle2 className="w-6 h-6 text-blue-600 flex-shrink-0 mt-1" />
                  <div>
                    <h4 className="font-bold text-gray-900 mb-1">Started as SAP B1 Specialists</h4>
                    <p className="text-sm text-gray-600">Built our reputation on deep SAP Business One expertise</p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <CheckCircle2 className="w-6 h-6 text-blue-600 flex-shrink-0 mt-1" />
                  <div>
                    <h4 className="font-bold text-gray-900 mb-1">500+ Successful Transformations</h4>
                    <p className="text-sm text-gray-600">Manufacturing, distribution, and services businesses thriving</p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <CheckCircle2 className="w-6 h-6 text-blue-600 flex-shrink-0 mt-1" />
                  <div>
                    <h4 className="font-bold text-gray-900 mb-1">World-Class Partnerships</h4>
                    <p className="text-sm text-gray-600">SAP Premier, AWS Advanced, Boyum Gold Partner status</p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <CheckCircle2 className="w-6 h-6 text-blue-600 flex-shrink-0 mt-1" />
                  <div>
                    <h4 className="font-bold text-gray-900 mb-1">Great Place to Work Certified</h4>
                    <p className="text-sm text-gray-600">Award-winning culture delivering exceptional customer support</p>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Our Values - Updated to Blue Theme */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="text-center mb-16">
            <span className="inline-block px-4 py-2 bg-blue-100 text-blue-600 rounded-full text-sm font-semibold mb-4">Our Values</span>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">What Drives Us</h2>
          </div>
          <div className="grid md:grid-cols-2 gap-6">
            {values.map((value, idx) => (
              <motion.div key={idx} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, delay: idx * 0.1 }} viewport={{ once: true }} className="flex items-start gap-6 p-8 bg-gradient-to-br from-slate-50 to-white rounded-xl border border-slate-200 hover:border-sky-300 hover:shadow-lg transition-all">
                <div className="w-16 h-16 rounded-xl bg-gradient-to-br from-sky-500/10 to-blue-500/10 flex items-center justify-center flex-shrink-0">
                  <value.icon className="w-8 h-8 text-sky-600" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">{value.title}</h3>
                  <p className="text-gray-600">{value.desc}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Technology Partners - Blue Theme */}
      <section className="py-20 bg-gradient-to-b from-gray-50 to-white">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="text-center mb-16">
            <span className="inline-block px-4 py-2 bg-blue-100 text-blue-600 rounded-full text-sm font-semibold mb-4">Technology Partners</span>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Partnering with World-Class Leaders</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">We combine SAP excellence, AWS cloud power, and Boyum innovation to deliver the best</p>
          </div>

          {/* 3 Partner Cards - Simplified */}
          <div className="grid lg:grid-cols-3 gap-8">
            {[
              { name: 'SAP', badge: 'Premier Partner', color: 'from-blue-600 to-sky-600', logo: '/sap-logo.svg' },
              { name: 'AWS', badge: 'Advanced Partner', color: 'from-sky-600 to-cyan-600', logo: '/aws-logo.svg' },
              { name: 'Boyum', badge: 'Gold Partner', color: 'from-blue-500 to-blue-600', logo: '/boyum-logo.png' }
            ].map((partner, idx) => (
              <motion.div key={idx} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, delay: idx * 0.1 }} viewport={{ once: true }} className="bg-white rounded-2xl border-2 border-slate-200 hover:border-sky-300 shadow-lg hover:shadow-xl transition-all overflow-hidden">
                <div className={`h-64 bg-gradient-to-br ${partner.color} flex items-center justify-center p-8`}>
                  <div className="w-full">
                    <div className="bg-white rounded-xl p-6 mb-4 mx-auto max-w-xs flex items-center justify-center" style={{ minHeight: '120px' }}>
                      <img src={partner.logo} alt={partner.name} className="max-h-20 w-auto mx-auto" />
                    </div>
                    <div className="text-center">
                      <span className="inline-block px-4 py-2 bg-white/20 backdrop-blur-sm text-white rounded-full text-sm font-semibold">{partner.badge}</span>
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Great Place to Work - C4P Style */}
      <section className="py-20 bg-gradient-to-b from-sky-50 via-blue-50 to-sky-50">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="text-center mb-16">
            <span className="inline-block px-4 py-2 bg-blue-100 text-blue-600 rounded-full text-sm font-semibold mb-4">Award-Winning Culture</span>
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">A Great Place to Work</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto mb-8">
              Recognised as one of the UK's best workplaces-because happy teams deliver exceptional customer support
            </p>
            <p className="text-gray-700 max-w-4xl mx-auto leading-relaxed">
              <strong>Great Place to Work Certification</strong> isn't just an award-it's independent validation that our employees trust leadership, feel valued, and genuinely love coming to work. This translates directly to <strong>better support, deeper expertise, and stronger partnerships for you</strong>.
            </p>
          </div>

          {/* 4 Badge Cards */}
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
            {[
              { img: '/best-workplaces-small.png', title: 'Top 100 Small Business', desc: 'Recognised for exceptional workplace culture' },
              { img: '/best-workplaces-wellbeing.png', title: 'Top 100 for Wellbeing', desc: 'Prioritising employee health and happiness' },
              { img: '/best-workplaces-tech.png', title: 'Top 100 in Tech', desc: 'Leading the way in technology sector culture' },
              { img: '/best-workplaces-development.png', title: 'Top 100 for Development', desc: 'Investing in continuous learning and growth' }
            ].map((badge, idx) => (
              <motion.div key={idx} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, delay: idx * 0.1 }} viewport={{ once: true }} className="bg-white rounded-2xl p-6 shadow-lg border border-slate-200 hover:shadow-xl transition-all">
                <div className="mb-4">
                  <img src={badge.img} alt={badge.title} className="w-full h-auto" />
                </div>
                <h3 className="font-bold text-gray-900 text-center mb-2">{badge.title}</h3>
                <p className="text-sm text-gray-600 text-center">{badge.desc}</p>
              </motion.div>
            ))}
          </div>

          {/* 3 Benefit Cards Below */}
          <div className="grid md:grid-cols-3 gap-6 max-w-5xl mx-auto">
            {[
              { icon: Users, title: 'High Staff Retention', desc: 'Our AWS and SAP certified engineers have been with us for years. This continuity means <strong>you always work with experienced professionals who know your environment</strong>.' },
              { icon: Award, title: 'Consistent Excellence', desc: 'Happy employees provide better service. Our award-winning culture ensures <strong>every interaction you have is with engaged, motivated professionals</strong>.' },
              { icon: Target, title: 'Partner Benefits', desc: 'When our team thrives, our partners thrive. <strong>Research shows certified companies deliver superior customer service and business results</strong>.' }
            ].map((benefit, idx) => (
              <motion.div key={idx} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, delay: idx * 0.1 }} viewport={{ once: true }} className="bg-white rounded-2xl p-8 shadow-lg border border-slate-200 hover:shadow-xl transition-all">
                <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-sky-500 to-blue-600 flex items-center justify-center mb-6 mx-auto">
                  <benefit.icon className="w-8 h-8 text-white" />
                </div>
                <h3 className="font-bold text-gray-900 mb-3 text-center text-lg">{benefit.title}</h3>
                <p className="text-sm text-gray-700 text-center leading-relaxed" dangerouslySetInnerHTML={{ __html: benefit.desc }} />
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-20 bg-gradient-to-b from-gray-50 to-white">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-stretch">
            <motion.div initial={{ opacity: 0, x: -30 }} whileInView={{ opacity: 1, x: 0 }} transition={{ duration: 0.6 }} viewport={{ once: true }} className="relative group flex">
              <div className="relative rounded-2xl overflow-hidden shadow-2xl w-full">
                <img src="https://images.unsplash.com/photo-1551434678-e076c223a692?w=800&q=85" alt="Team Success" className="w-full h-full object-cover transform group-hover:scale-105 transition-transform duration-500" loading="lazy" />
                <div className="absolute inset-0 bg-gradient-to-br from-blue-600/20 to-transparent" />
              </div>
            </motion.div>
            <motion.div initial={{ opacity: 0, x: 30 }} whileInView={{ opacity: 1, x: 0 }} transition={{ duration: 0.6 }} viewport={{ once: true }} className="flex flex-col justify-center">
              <span className="inline-block px-4 py-2 bg-blue-100 text-blue-600 rounded-full text-sm font-semibold mb-4 w-fit">Why Choose Ascarii</span>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">We'll Never Leave You</h2>
              <p className="text-lg text-gray-600 mb-6">Our commitment doesn't end at go-live. We're your long-term partner, invested in your ongoing success and growth.</p>
              <div className="space-y-3">
                {['15+ years SAP Business One expertise', '500+ successful implementations', 'SAP Premier Partner with multiple awards', 'AWS Advanced Partner - 99.995% uptime', 'Specialist manufacturing and distribution solutions', 'Comprehensive support - always here when you need us', 'Great Place to Work certified company'].map((point, i) => (
                  <div key={i} className="flex items-start space-x-3">
                    <CheckCircle2 className="w-6 h-6 text-blue-600 flex-shrink-0 mt-0.5" />
                    <p className="text-gray-700">{point}</p>
                  </div>
                ))}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-gradient-to-br from-gray-100 via-blue-50 to-gray-100">
        <div className="container mx-auto px-4 lg:px-8 max-w-4xl text-center">
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">Ready to Start Your Transformation?</h2>
          <p className="text-xl text-gray-600 mb-8">Let's discuss how we can help your business thrive</p>
          <div className="flex flex-wrap justify-center gap-4">
            <a href="/contact" className="inline-flex items-center justify-center rounded-xl bg-blue-600 text-white px-8 py-4 text-lg font-semibold hover:bg-blue-700 shadow-lg transition-colors">Get in Touch</a>
            <a href="/sap-business-one" className="inline-flex items-center justify-center rounded-xl border-2 border-blue-600 text-blue-600 px-8 py-4 text-lg font-semibold hover:bg-blue-50 shadow-lg transition-colors">Explore Our Solutions</a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AboutUsPage;
